import type { DashboardMonthlyOverviewDto, DashboardSummaryDto } from '@gty/common';

function addDays(days: number): Date {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date;
}

function isoDateFromToday(days: number): string {
  const date = addDays(days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function isoDateTimeFromToday(days: number, hour: number, minute: number): string {
  const date = addDays(days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
}

function monthLabel(offset = 0): string {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() + offset);
  return date.toLocaleDateString('en-IN', { month: 'long' });
}

function shortMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-IN', { month: 'short' });
}

function trendSubtitle(): string {
  const start = new Date();
  start.setDate(1);
  start.setMonth(start.getMonth() - 11);
  const end = new Date();
  return `${shortMonthLabel(start)} ${start.getFullYear()} - ${shortMonthLabel(end)} ${end.getFullYear()} · net of taxes`;
}

function daysTag(days: number): string {
  if (days === 0) return 'TODAY';
  if (days === 1) return '1 DAY';
  return `${days} DAYS`;
}

/**
 * Dashboard fixtures — figures, copy and dates lifted from `Guruji ERP.dc.html`.
 *
 * Typed as the REAL `DashboardSummaryDto` from `@gty/common`, not a hand-rolled
 * shape: when the contract changes this file fails to compile instead of
 * quietly drifting from the API it stands in for.
 *
 * Nothing outside `services/` may hold business figures, and no component may
 * import this file directly — see `services/index.ts`.
 */
export const DASHBOARD_MOCK: DashboardSummaryDto = {
  kpis: [
    {
      key: 'total-leads',
      module: 'LEAD_CRM',
      label: 'Total Leads',
      value: 148,
      unit: 'count',
      deltaPct: 18,
      display: '148',
      caption: '18% vs last month',
      href: '/leads',
    },
    {
      key: 'active-bookings',
      module: 'BOOKING_MANAGEMENT',
      label: 'Active Bookings',
      value: 37,
      unit: 'count',
      deltaPct: null,
      display: '37',
      caption: '6 confirmed this week',
      href: '/bookings',
    },
    {
      key: 'revenue-current-month',
      module: 'PAYMENTS_RECEIPTS',
      label: `Revenue · ${monthLabel()}`,
      value: 8240000,
      unit: 'currency',
      deltaPct: 28.8,
      display: '₹82,40,000',
      caption: `28.8% vs ${monthLabel(-1)}`,
    },
    {
      key: 'pending-payments',
      module: 'PAYMENTS_RECEIPTS',
      label: 'Pending Payments',
      value: 1470000,
      unit: 'currency',
      deltaPct: null,
      display: '₹14,70,000',
      caption: '3 overdue · 5 due soon',
      emphasis: 'alert',
      href: '/payments',
    },
    {
      key: 'upcoming-departures',
      module: 'TOUR_MASTER',
      label: 'Upcoming Departures',
      value: 9,
      unit: 'count',
      deltaPct: null,
      display: '9',
      caption: 'next today',
    },
  ],

  trend: {
    title: 'Revenue trend',
    subtitle: trendSubtitle(),
    /**
     * Values are back-derived from the design's SVG polyline. The chart plots
     * 0–90L across y=200 (zero) to y=32 (90L), so value = (200 − y) / 168 × 90L.
     */
    points: [
      { label: 'Aug', current: 3860000, previous: 2570000 },
      { label: 'Sep', current: 4230000, previous: 3000000 },
      { label: 'Oct', current: 6160000, previous: 4290000 },
      { label: 'Nov', current: 7500000, previous: 5300000 },
      { label: 'Dec', current: 5890000, previous: 4390000 },
      { label: 'Jan', current: 4980000, previous: 3700000 },
      { label: 'Feb', current: 4450000, previous: 3210000 },
      { label: 'Mar', current: 5250000, previous: 3860000 },
      { label: 'Apr', current: 6860000, previous: 4870000 },
      { label: 'May', current: 7180000, previous: 5300000 },
      { label: 'Jun', current: 6480000, previous: 4710000 },
      { label: 'Jul', current: 8300000, previous: 6000000 },
    ],
  },

  calendar: [
    {
      id: 'c1',
      date: isoDateFromToday(0),
      kind: 'DEPARTURE',
      title: 'Bali Honeymoon Escape',
      subtitle: 'Nisha Pillai +1 · BK-2607-0088',
      tag: daysTag(0),
      href: '/bookings',
    },
    {
      id: 'c2',
      date: isoDateFromToday(1),
      kind: 'DEPARTURE',
      title: 'Manali & Solang Valley',
      subtitle: 'Group of 14 · BK-2607-0079',
      tag: daysTag(1),
      href: '/bookings',
    },
    {
      id: 'c3',
      date: isoDateFromToday(7),
      kind: 'DEPARTURE',
      title: 'Rajasthan Royal Circuit',
      subtitle: 'Aditya Joshi +3 · BK-2607-0084',
      tag: 'PAID',
      href: '/bookings',
    },
    {
      id: 'c4',
      date: isoDateFromToday(13),
      kind: 'DEPARTURE',
      title: 'Kerala Backwaters & Munnar',
      subtitle: 'Kulkarni family · BK-2607-0090',
      tag: daysTag(13),
      href: '/bookings',
    },
    {
      id: 'c5',
      date: isoDateFromToday(0),
      kind: 'PAYMENT',
      title: 'Payment overdue — INV-2607-0141',
      subtitle: 'Vikram Desai · ₹1,12,000 · 6 days past due',
      tag: 'OVERDUE',
      href: '/payments',
    },
    {
      id: 'c6',
      date: isoDateFromToday(6),
      kind: 'RETURN',
      title: 'Bali Honeymoon Escape — return',
      subtitle: 'Nisha Pillai +1 · DPS → BOM',
      tag: 'RETURN',
      href: '/bookings',
    },
  ],

  departures: [
    {
      id: 'd1',
      date: isoDateFromToday(0),
      title: 'Bali Honeymoon Escape',
      subtitle: 'Nisha Pillai +1 · BK-2607-0088 · Imran Shaikh',
      tag: daysTag(0),
      tone: 'warning',
    },
    {
      id: 'd2',
      date: isoDateFromToday(1),
      title: 'Manali & Solang Valley',
      subtitle: 'Group of 14 · BK-2607-0079 · Volvo from Delhi',
      tag: daysTag(1),
      tone: 'neutral',
    },
    {
      id: 'd3',
      date: isoDateFromToday(7),
      title: 'Rajasthan Royal Circuit',
      subtitle: 'Aditya Joshi +3 · BK-2607-0084 · Innova Crysta',
      tag: 'PAID',
      tone: 'success',
    },
    {
      id: 'd4',
      date: isoDateFromToday(13),
      title: 'Kerala Backwaters & Munnar',
      subtitle: 'Kulkarni family · BK-2607-0090 · houseboat held',
      tag: daysTag(13),
      tone: 'neutral',
    },
  ],

  recentLeads: [
    {
      id: 'l1',
      name: 'Ananya Sharma',
      city: 'Pune',
      interest: 'Char Dham Yatra · 6 pax',
      source: 'Instagram',
      value: 412000,
      stage: 'NEW',
    },
    {
      id: 'l2',
      name: 'Vikram Desai',
      city: 'Ahmedabad',
      interest: 'Kedarnath Yatra · 4 pax',
      source: 'Referral',
      value: 268000,
      stage: 'QUOTED',
    },
    {
      id: 'l3',
      name: 'Tanvi Bhatt',
      city: 'Surat',
      interest: 'Bali Honeymoon · 2 pax',
      source: 'Instagram',
      value: 186000,
      stage: 'NEW',
    },
    {
      id: 'l4',
      name: 'Rohit Menon',
      city: 'Kochi',
      interest: 'Kerala Backwaters · 8 pax',
      source: 'Website',
      value: 340000,
      stage: 'CONTACTED',
    },
    {
      id: 'l5',
      name: 'Aditya Joshi',
      city: 'Mumbai',
      interest: 'Rajasthan Royal Circuit · 4 pax',
      source: 'Walk-in',
      value: 209200,
      stage: 'CONVERTED',
    },
    {
      id: 'l6',
      name: 'Sneha Rao',
      city: 'Bengaluru',
      interest: 'Manali & Solang · 14 pax',
      source: 'Google Ads',
      value: 596000,
      stage: 'CONTACTED',
    },
  ],

  recentActivity: [
    {
      id: 'a1',
      type: 'BOOKING',
      title: 'Booking confirmed — Rajasthan Royal Circuit',
      description: 'BK-2607-0084 · 4 passengers · departure 8 Aug',
      occurredAt: isoDateTimeFromToday(0, 9, 12),
    },
    {
      id: 'a2',
      type: 'PAYMENT',
      title: '₹2,09,200 received from Aditya Joshi',
      description: 'NEFT · receipt RCP-2607-0233 issued',
      occurredAt: isoDateTimeFromToday(0, 8, 40),
    },
    {
      id: 'a3',
      type: 'LEAD',
      title: '2 new leads from Instagram',
      description: 'Ananya Sharma, Tanvi Bhatt · unassigned',
      occurredAt: isoDateTimeFromToday(-1, 17, 5),
    },
  ],

  quickActions: [
    { key: 'new-lead', label: 'New Lead', module: 'LEAD_CRM', requiredLevel: 'FULL' },
    { key: 'new-booking', label: 'New Booking', module: 'BOOKING_MANAGEMENT', requiredLevel: 'FULL' },
  ],

  // Hand-authored like the rest of this fixture, not formula-derived.
  headerSummary: { departuresThisWeek: 2, paymentsNeedingChasing: 3, leadsWaitingOnYou: 4 },
};

/** The month carousel's mock data (Part B) — same `VITE_USE_MOCK_DASHBOARD` shell fixture, not formula-derived. */
export const DASHBOARD_MONTHS_MOCK: DashboardMonthlyOverviewDto = {
  months: [
    {
      monthKey: '2026-06', label: monthLabel(-2), inProgress: false, bookingsCount: 9,
      bookedInclGst: 820_000, bookedExclGst: 780_952.38, collected: 640_000, refunds: 0, netCollected: 640_000,
      cancellations: 1, expenses: 210_000, profit: 570_952.38, bookingsWithNoCosts: 1,
    },
    {
      monthKey: '2026-07', label: monthLabel(-1), inProgress: false, bookingsCount: 12,
      bookedInclGst: 1_140_000, bookedExclGst: 1_085_714.29, collected: 980_000, refunds: 36_000, netCollected: 944_000,
      cancellations: 0, expenses: 340_000, profit: 745_714.29, bookingsWithNoCosts: 2,
    },
    {
      monthKey: '2026-08', label: monthLabel(0), inProgress: true, bookingsCount: 5,
      bookedInclGst: 410_000, bookedExclGst: 390_476.19, collected: 250_000, refunds: 0, netCollected: 250_000,
      cancellations: 0, expenses: 60_000, profit: 330_476.19, bookingsWithNoCosts: 3,
    },
  ],
};
